import {
  getDynamicJsonData,
  getDynamicParametersJsonData,
  getDynamicRatesJsonData,
} from "api/dataVisualisation/dynamic/dynamic";
import useRequest, { RequestStatus } from "hooks/useRequest";
import { DynamicDataModel } from "models/bmuData/dynamicDataModel";
import { DynamicParametersDataModel } from "models/bmuData/dynamicParametersDataModel";
import { DynamicRatesDataModel } from "models/bmuData/dynamicRatesDataModel";
import DateFilterModel from "models/filterModels/dateFilterModel";
import { useCallback } from "react";
import featureFlags from "utils/featureFlags";
import { combineRequestStatuses } from "utils/requestHelpers";

export type DynamicTabData = {
  dynamicData: DynamicDataModel[];
  dynamicRatesData: DynamicRatesDataModel[];
  dynamicParametersData: DynamicParametersDataModel[];
};

const useDynamicData = (
  dateFilter: DateFilterModel | null,
  bmUnit: string | null
): {
  request: () => void;
  data: DynamicTabData | null;
  status: RequestStatus;
} => {
  const {
    data: dynamicData,
    status: dynamicStatus,
    request: fetchDynamicData,
  } = useRequest(getDynamicJsonData);

  const {
    data: dynamicRatesData,
    status: dynamicRatesStatus,
    request: fetchDynamicRatesData,
  } = useRequest(getDynamicRatesJsonData);

  const {
    data: dynamicParametersData,
    status: dynamicParametersStatus,
    request: fetchDynamicParametersData,
  } = useRequest(getDynamicParametersJsonData);

  const data =
    dynamicData?.data && dynamicRatesData?.data
      ? {
          dynamicData: dynamicData.data,
          dynamicRatesData: dynamicRatesData.data,
          dynamicParametersData: dynamicParametersData?.data || [],
        }
      : null;

  const status = combineRequestStatuses(
    dynamicStatus,
    dynamicRatesStatus,
    dynamicParametersStatus
  );

  const request = useCallback(() => {
    if (dateFilter && bmUnit) {
      fetchDynamicData(dateFilter, bmUnit);
      fetchDynamicRatesData(dateFilter, bmUnit);
      // TODO 316294: Remove if condition and always fetch dynamic parameters data
      if (featureFlags.displayDynamicParameters) {
        fetchDynamicParametersData(dateFilter, bmUnit);
      }
    }
  }, [
    bmUnit,
    dateFilter,
    fetchDynamicData,
    fetchDynamicParametersData,
    fetchDynamicRatesData,
  ]);

  return {
    data,
    status,
    request,
  };
};

export default useDynamicData;
